import React, { useMemo } from 'react';

function resolveByKeys(source, keys) {
    if (!source || typeof source !== 'object') {
        return undefined;
    }
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            return source[key];
        }
    }
    return undefined;
}

function isDoneStatus(value) {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return value > 0;
    }
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
        return false;
    }
    const compact = normalized.replace(/[\s-]+/g, '_');
    const notDoneTokens = ['not_done', 'notdone', 'no', 'pending'];
    if (notDoneTokens.includes(compact)) {
        return false;
    }
    const doneTokens = ['done', 'yes', 'approved', 'completed', 'completed'];
    return doneTokens.includes(compact);
}

function isRejectedStatus(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
        return false;
    }

    const rejectTokens = ['rejected', 'reject', 'denied', 'not approved'];
    return rejectTokens.some((token) => normalized.includes(token));
}

function isRowRejected(row) {
    return [
        'from_college',
        'to_college',
        'ct_admin',
        'super_admin',
        'profile_status',
        'payment_status',
        'doc_upload_status',
    ].some((key) => isRejectedStatus(resolveByKeys(row, [key])));
}

export default function StudentAbstractReport({ rows = [] }) {
    const counts = useMemo(() => {
        const totalStudents = rows.length;
        const detailsGiven = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['profile_status']))).length;
        const documentsUploaded = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['doc_upload_status']))).length;
        const paymentDone = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['payment_status']))).length;
        const fromCollegeDone = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['from_college']))).length;
        const toCollegeDone = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['to_college']))).length;
        const cteApproved = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['ct_admin']))).length;
        const superAdminApproved = rows.filter((row) => isDoneStatus(resolveByKeys(row, ['super_admin']))).length;
        const rejected = rows.filter(isRowRejected).length;
        const notAppliedForTransfer = Math.max(0, totalStudents - paymentDone);
        const processing = Math.max(0, totalStudents - detailsGiven);

        return {
            totalStudents,
            detailsGiven,
            documentsUploaded,
            paymentDone,
            fromCollegeDone,
            toCollegeDone,
            cteApproved,
            superAdminApproved,
            rejected,
            notAppliedForTransfer,
            processing,
        };
    }, [rows]);

    const reportDate = new Date().toLocaleDateString('en-GB');
    const reportRows = [
        { label: 'Registered Students', value: counts.totalStudents },
        { label: 'Student Academic Profile Filled [Details Given]', value: counts.detailsGiven },
        { label: 'Documents Uploaded', value: counts.documentsUploaded },
        { label: 'Payment Done', value: counts.paymentDone },
        { label: 'Not Applied for transfer [(1)-(4)]', value: counts.notAppliedForTransfer },
        { label: 'From College NOC verification Done', value: counts.fromCollegeDone },
        { label: 'To College NOC verification Done', value: counts.toCollegeDone },
        {
            label: 'Rejected (at From College / To College / CTE Admin / SUPER Admin) Levels',
            value: counts.rejected,
        },
        { label: 'Processing [(1)-(2)]', value: counts.processing },
        { label: 'Approved at CTE Level', value: counts.cteApproved },
        { label: 'Approved at SUPER ADMIN Level', value: counts.superAdminApproved },
    ];

    return (
        <div className="student-abstract-report">
            <div className="mb-4">
                <h5 className="mb-1">Abstract (as on {reportDate})</h5>
            </div>

            <div className="table-responsive">
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th style={{ backgroundColor: '#2f5d50', color: 'white', width: '10%', textAlign: 'center' }}>S.No</th>
                            <th style={{ backgroundColor: '#2f5d50', color: 'white' }}>Application Status</th>
                            <th style={{ backgroundColor: '#2f5d50', color: 'white', width: '15%', textAlign: 'center' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportRows.map((item, index) => (
                            <tr key={item.label}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td>{item.label}</td>
                                <td style={{ textAlign: 'center' }}>{item.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
